import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Skeleton,
  InputAdornment,
  Snackbar,
  Autocomplete,
  Avatar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  TaskAlt as TaskAltIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import api, { enhancedApi } from '../api';
import TaskForm from '../components/TaskForm';
import PageLayout from '../components/ui/PageLayout';
import dayjs from 'dayjs';

const FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'my', label: 'My Tasks' },
  { key: 'due_today', label: 'Due Today' },
  { key: 'due_week', label: 'Due This Week' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'completed', label: 'Completed' },
];

const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];

const normPriorityKey = (p) => String(p || 'low').toLowerCase();

const getPriorityChip = (p) => {
  const k = normPriorityKey(p);
  const order = PRIORITY_ORDER.includes(k) ? k : 'low';
  const label = order.charAt(0).toUpperCase() + order.slice(1);
  const color =
    order === 'urgent' ? 'error' : order === 'high' ? 'warning' : order === 'medium' ? 'info' : 'default';
  return { label, color, variant: color === 'default' ? 'outlined' : 'filled' };
};

const STATUS_CHIP = {
  PENDING: { label: 'Not Started', color: 'default', variant: 'outlined' },
  IN_PROGRESS: { label: 'In Progress', color: 'info' },
  NEEDS_REVIEW: { label: 'Needs Review', color: 'info' },
  BLOCKED: { label: 'Blocked', color: 'error' },
  COMPLETED: { label: 'Complete', color: 'success' },
  CANCELLED: { label: 'Cancelled', color: 'default', variant: 'outlined' },
};

const isCompletedStatus = (status) =>
  status === 'COMPLETED' || status === 'CANCELLED';

const startOfToday = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};

const startOfWeekMonday = (ref = new Date()) => {
  const x = new Date(ref);
  const day = x.getDay();
  const diff = x.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(x.setDate(diff));
  mon.setHours(0, 0, 0, 0);
  return mon;
};

const endOfWeekSunday = (weekStart) => {
  const e = new Date(weekStart);
  e.setDate(e.getDate() + 7);
  return e;
};

const calendarDayEqual = (a, b) => {
  const da = new Date(a);
  const db = new Date(b);
  da.setHours(0, 0, 0, 0);
  db.setHours(0, 0, 0, 0);
  return da.getTime() === db.getTime();
};

const getCurrentUserId = () => {
  const raw = localStorage.getItem('userId');
  if (raw != null && raw !== '') return Number(raw);
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.id != null ? Number(u.id) : null;
  } catch {
    return null;
  }
};

const initialsFromName = (name) => {
  if (!name || !String(name).trim()) return '?';
  const p = String(name).trim().split(/\s+/).filter(Boolean);
  if (p.length >= 2) return `${p[0][0]}${p[1][0]}`.toUpperCase();
  return String(name).slice(0, 2).toUpperCase();
};

const SummaryStatCard = ({ label, count, borderColor, caption }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      borderLeftWidth: 4,
      borderLeftStyle: 'solid',
      borderLeftColor: borderColor,
      bgcolor: 'background.paper',
      height: '100%',
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
      {count}
    </Typography>
    {caption ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {caption}
      </Typography>
    ) : null}
  </Paper>
);

const TasksPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const createParam = searchParams.get('create') === '1';

  const [tasks, setTasks] = useState([]);
  const [totalTaskCount, setTotalTaskCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterChip, setFilterChip] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(createParam);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [editTask, setEditTask] = useState(null);
  const [editDue, setEditDue] = useState('');
  const [editPriority, setEditPriority] = useState('medium');
  const [editAssignee, setEditAssignee] = useState(null);
  const [staffOptions, setStaffOptions] = useState([]);
  const [editDetailLoading, setEditDetailLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTask, setDeleteTask] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const currentUserId = useMemo(() => getCurrentUserId(), []);

  useEffect(() => {
    setShowCreateForm(createParam);
  }, [createParam]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let all = [];
      let serverTotal = 0;
      let page = 1;
      let hasMore = true;
      const maxPages = 20;

      while (hasMore && page <= maxPages) {
        const res = await api.get('/api/multi-tenant/workflow-tasks/', {
          params: { page, page_size: 200 },
        });
        const body = res.data;
        const chunk = Array.isArray(body) ? body : body?.results ?? [];
        if (typeof body?.count === 'number') serverTotal = body.count;
        all = all.concat(chunk);
        hasMore = Boolean(body?.next) && chunk.length > 0;
        page += 1;
      }

      setTasks(all);
      setTotalTaskCount(serverTotal || all.length);
    } catch {
      setError('Failed to load tasks');
      setTasks([]);
      setTotalTaskCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const loadStaff = useCallback(async () => {
    try {
      const res = await enhancedApi.getUsers();
      const userList = Array.isArray(res.data) ? res.data : res.data?.users || res.data?.data || [];
      const opts = userList.map((u) => ({
        id: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || `User ${u.id}`,
      }));
      setStaffOptions(opts);
      return opts;
    } catch {
      setStaffOptions([]);
      return [];
    }
  }, []);

  const openEdit = useCallback(
    async (task) => {
      setEditTask(task);
      setEditDetailLoading(true);
      try {
        const [taskRes, staffOpts] = await Promise.all([
          api.get(`/api/multi-tenant/tasks/${task.id}/`),
          loadStaff(),
        ]);
        const t = taskRes.data || {};
        setEditDue(t.due_date ? String(t.due_date).split('T')[0] : '');
        setEditPriority((t.priority || task.priority || 'medium').toLowerCase());
        const aid = t.assigned_to;
        if (aid) {
          const opt = staffOpts.find((s) => s.id === aid);
          setEditAssignee(opt || { id: aid, label: t.assigned_to_name || `User ${aid}` });
        } else {
          setEditAssignee(null);
        }
      } catch {
        setEditDue(task.due_date ? dayjs(task.due_date).format('YYYY-MM-DD') : '');
        setEditPriority(String(task.priority || 'medium').toLowerCase());
        setEditAssignee(null);
      } finally {
        setEditDetailLoading(false);
      }
    },
    [loadStaff]
  );

  const handleComplete = useCallback(async (taskId) => {
    setActionLoadingId(taskId);
    try {
      await api.patch(`/api/multi-tenant/tasks/${taskId}/update/`, { status: 'COMPLETED' });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: 'COMPLETED' } : t)));
      setSnackbar({ open: true, message: 'Task completed', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || 'Failed to complete task',
        severity: 'error',
      });
    } finally {
      setActionLoadingId(null);
    }
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editTask) return;
    setEditSaving(true);
    try {
      await api.patch(`/api/multi-tenant/tasks/${editTask.id}/update/`, {
        due_date: editDue || null,
        priority: editPriority,
        assigned_to: editAssignee?.id || null,
      });
      setSnackbar({ open: true, message: 'Task updated', severity: 'success' });
      setEditTask(null);
      fetchTasks();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to update task',
        severity: 'error',
      });
    } finally {
      setEditSaving(false);
    }
  }, [editTask, editDue, editPriority, editAssignee, fetchTasks]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTask) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/api/multi-tenant/tasks/${deleteTask.id}/update/`);
      setSnackbar({ open: true, message: 'Task deleted', severity: 'success' });
      setDeleteTask(null);
      setSelectedIds((ids) => ids.filter((id) => id !== deleteTask.id));
      fetchTasks();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to delete task',
        severity: 'error',
      });
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTask, fetchTasks]);

  const handleFormClose = () => {
    setShowCreateForm(false);
    setSearchParams({});
    fetchTasks();
  };

  const summary = useMemo(() => {
    const today = startOfToday();
    const ws = startOfWeekMonday(today);
    const we = endOfWeekSunday(ws);

    const overdue = tasks.filter(
      (t) => t.due_date && new Date(t.due_date) < today && !isCompletedStatus(t.status)
    ).length;

    const dueToday = tasks.filter(
      (t) => t.due_date && calendarDayEqual(t.due_date, today) && !isCompletedStatus(t.status)
    ).length;

    const completedThisWeek = tasks.filter((t) => {
      if (t.status !== 'COMPLETED') return false;
      if (!t.due_date) return false;
      const due = new Date(t.due_date);
      return due >= ws && due < we;
    }).length;

    return { overdue, dueToday, completedThisWeek };
  }, [tasks]);

  const filteredRows = useMemo(() => {
    const today = startOfToday();
    const ws = startOfWeekMonday(today);
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);

    let list = tasks;

    if (filterChip === 'my' && currentUserId != null) {
      list = list.filter((t) => Number(t.assigned_to) === Number(currentUserId));
    } else if (filterChip === 'overdue') {
      list = list.filter(
        (t) => t.due_date && new Date(t.due_date) < today && !isCompletedStatus(t.status)
      );
    } else if (filterChip === 'due_today') {
      list = list.filter(
        (t) => t.due_date && calendarDayEqual(t.due_date, today) && !isCompletedStatus(t.status)
      );
    } else if (filterChip === 'due_week') {
      list = list.filter((t) => {
        if (!t.due_date || isCompletedStatus(t.status)) return false;
        const d = new Date(t.due_date);
        d.setHours(0, 0, 0, 0);
        return d >= today && d <= weekEnd;
      });
    } else if (filterChip === 'completed') {
      list = list.filter((t) => isCompletedStatus(t.status));
    }

    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (t) =>
        (t.task_name || '').toLowerCase().includes(q) ||
        (t.client_name || '').toLowerCase().includes(q) ||
        (t.workflow_name || '').toLowerCase().includes(q) ||
        (t.assigned_to_name || '').toLowerCase().includes(q)
    );
  }, [tasks, filterChip, search, currentUserId]);

  const formatDue = useCallback((d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  const openBulkAssign = async () => {
    if (selectedIds.length === 0) return;
    await loadStaff();
    setBulkAssignee(null);
    setAssignDialogOpen(true);
  };

  const runBulkAssign = async () => {
    if (!bulkAssignee?.id) {
      setSnackbar({ open: true, message: 'Choose a user', severity: 'warning' });
      return;
    }
    const ids = [...selectedIds];
    const total = ids.length;
    setBulkBusy(true);
    let ok = 0;
    for (const id of ids) {
      try {
        await api.patch(`/api/multi-tenant/tasks/${id}/update/`, { assigned_to: bulkAssignee.id });
        ok++;
      } catch {
        /* skip */
      }
    }
    setBulkBusy(false);
    setAssignDialogOpen(false);
    setSelectedIds([]);
    fetchTasks();
    setSnackbar({ open: true, message: `Assigned ${ok} of ${total} task(s)`, severity: 'success' });
  };

  const runBulkComplete = async () => {
    const targets = tasks.filter(
      (t) => selectedIds.includes(t.id) && !isCompletedStatus(t.status)
    );
    if (targets.length === 0) {
      setSnackbar({ open: true, message: 'No selected tasks to complete', severity: 'warning' });
      return;
    }
    setBulkBusy(true);
    let ok = 0;
    for (const t of targets) {
      try {
        await api.patch(`/api/multi-tenant/tasks/${t.id}/update/`, { status: 'COMPLETED' });
        ok++;
      } catch {
        /* skip */
      }
    }
    setBulkBusy(false);
    setSelectedIds([]);
    fetchTasks();
    setSnackbar({ open: true, message: `Completed ${ok} task(s)`, severity: 'success' });
  };

  const runBulkDelete = async () => {
    const ids = [...selectedIds];
    const total = ids.length;
    setBulkBusy(true);
    let ok = 0;
    for (const id of ids) {
      try {
        await api.delete(`/api/multi-tenant/tasks/${id}/update/`);
        ok++;
      } catch {
        /* skip */
      }
    }
    setBulkBusy(false);
    setBulkDeleteOpen(false);
    setSelectedIds([]);
    fetchTasks();
    setSnackbar({ open: true, message: `Deleted ${ok} of ${total} task(s)`, severity: 'success' });
  };

  const columns = useMemo(
    () => [
      {
        field: 'task_name',
        headerName: 'Task Name',
        flex: 1.15,
        minWidth: 170,
        renderCell: (params) => {
          const done = isCompletedStatus(params.row.status);
          return (
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                textDecoration: done ? 'line-through' : 'none',
                color: done ? 'text.disabled' : 'text.primary',
              }}
            >
              {params.row.task_name || `Task ${params.row.id}`}
            </Typography>
          );
        },
      },
      {
        field: 'client_name',
        headerName: 'Client',
        flex: 1,
        minWidth: 160,
        renderCell: (params) => {
          const cid = params.row.client_id;
          const name = params.row.client_name || '—';
          const initial = initialsFromName(name);
          return (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, py: 0.5 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                }}
              >
                {initial}
              </Avatar>
              {cid ? (
                <Typography
                  component={RouterLink}
                  to={`/client/${cid}`}
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {name}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {name}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 115,
        renderCell: (params) => {
          const { label, color, variant } = getPriorityChip(params.value);
          return <Chip label={label} color={color} size="small" variant={variant || 'filled'} />;
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 128,
        renderCell: (params) => {
          const st = params.value || 'PENDING';
          const cfg = STATUS_CHIP[st] || STATUS_CHIP.PENDING;
          return (
            <Chip label={cfg.label} color={cfg.color} size="small" variant={cfg.variant || 'filled'} />
          );
        },
      },
      {
        field: 'due_date',
        headerName: 'Due Date',
        width: 118,
        renderCell: (params) => {
          const raw = params.row.due_date;
          const done = isCompletedStatus(params.row.status);
          const today = startOfToday();
          let color = 'text.secondary';
          let fontWeight = 400;
          if (raw && !done) {
            if (new Date(raw) < today) {
              color = 'error.main';
              fontWeight = 700;
            } else if (calendarDayEqual(raw, today)) {
              color = 'warning.main';
              fontWeight = 700;
            }
          }
          return (
            <Typography variant="body2" color={color} fontWeight={fontWeight}>
              {formatDue(raw)}
            </Typography>
          );
        },
      },
      {
        field: 'assigned_to_name',
        headerName: 'Assigned',
        flex: 0.85,
        minWidth: 130,
        renderCell: (params) => {
          const nm = params.row.assigned_to_name;
          if (!nm) {
            return (
              <Typography variant="body2" color="text.disabled">
                —
              </Typography>
            );
          }
          const ini = initialsFromName(nm);
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'secondary.light', color: 'secondary.main' }}>
                {ini}
              </Avatar>
              <Typography variant="body2" color="text.secondary" noWrap>
                {nm}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 124,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;
          const done = isCompletedStatus(row.status);
          const busy = actionLoadingId === row.id;
          return (
            <Stack
              direction="row"
              className="task-row-actions"
              spacing={0}
              sx={{
                alignItems: 'center',
                height: '100%',
                opacity: 0,
                transition: 'opacity 0.12s ease-out',
                '.MuiDataGrid-row:hover &': { opacity: 1 },
              }}
            >
              {!done && (
                <Tooltip title="Complete">
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      disabled={busy}
                      onClick={() => handleComplete(row.id)}
                      aria-label="Mark complete"
                    >
                      {busy ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Edit">
                <IconButton size="small" color="inherit" onClick={() => openEdit(row)} aria-label="Edit task">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="inherit" onClick={() => setDeleteTask(row)} aria-label="Delete task">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [actionLoadingId, formatDue, handleComplete, openEdit]
  );

  if (loading) {
    return (
      <PageLayout maxWidth="none" sx={{ width: '100%' }}>
        <Box sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
            <Box>
              <Skeleton variant="text" width={200} height={36} />
              <Skeleton variant="text" width={160} height={22} />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="rounded" width={300} height={40} />
              <Skeleton variant="rounded" width={120} height={40} />
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} variant="rounded" width={96} height={32} />
            ))}
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={88} />
            ))}
          </Box>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider' }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" width="100%" height={48} sx={{ mb: 1 }} />
            ))}
          </Paper>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="none" sx={{ width: '100%' }}>
      <Box sx={{ width: '100%' }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalTaskCount} task{totalTaskCount === 1 ? '' : 's'} total
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ width: { xs: '100%', lg: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Filter this task list…"
              value={search}
              inputProps={{ 'aria-label': 'Filter tasks on this page' }}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateForm(true)}
              sx={{ flexShrink: 0 }}
            >
              Add Task
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          {FILTER_CHIPS.map((c) => (
            <Chip
              key={c.key}
              label={c.label}
              onClick={() => setFilterChip(c.key)}
              color={filterChip === c.key ? 'primary' : 'default'}
              variant={filterChip === c.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 2,
          }}
        >
          <SummaryStatCard label="Overdue" count={summary.overdue} borderColor="error.main" />
          <SummaryStatCard label="Due Today" count={summary.dueToday} borderColor="warning.main" />
          <SummaryStatCard label="Completed This Week" count={summary.completedThisWeek} borderColor="success.main" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedIds.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              px: 2,
              py: 1.5,
              mb: 2,
              borderRadius: 2,
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mr: 1 }}>
              {selectedIds.length} selected
            </Typography>
            <Button size="small" variant="outlined" onClick={openBulkAssign} disabled={bulkBusy}>
              Assign
            </Button>
            <Button size="small" variant="outlined" color="success" onClick={runBulkComplete} disabled={bulkBusy}>
              Complete
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={() => setBulkDeleteOpen(true)} disabled={bulkBusy}>
              Delete
            </Button>
          </Paper>
        )}

        {filteredRows.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              py: 10,
              textAlign: 'center',
              borderRadius: 2,
              borderColor: 'divider',
            }}
          >
            <TaskAltIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {tasks.length === 0 ? 'No tasks yet' : 'No matching tasks'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {tasks.length === 0
                ? 'Create a task or assign a workflow to get started.'
                : search.trim()
                  ? `No tasks match your search.`
                  : 'Try a different filter or search.'}
            </Typography>
            {tasks.length === 0 && !search.trim() && (
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setShowCreateForm(true)}>
                Add your first task
              </Button>
            )}
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              width: '100%',
              '& .MuiDataGrid-row:hover': {
                bgcolor: 'grey.50',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
              },
              '& .MuiDataGrid-cell': { py: 1 },
            }}
          >
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.id}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectedIds}
              onRowSelectionModelChange={(ids) => setSelectedIds(ids)}
              autoHeight
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-footerContainer': { borderTop: 1, borderColor: 'divider' },
              }}
            />
          </Paper>
        )}
      </Box>

      {showCreateForm && (
        <Dialog open onClose={handleFormClose} maxWidth="sm" fullWidth>
          <TaskForm onClose={handleFormClose} onFormSubmit={handleFormClose} mode="create" />
        </Dialog>
      )}

      <Dialog open={assignDialogOpen} onClose={() => !bulkBusy && setAssignDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign tasks</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={staffOptions}
            value={bulkAssignee}
            onChange={(_, v) => setBulkAssignee(v)}
            getOptionLabel={(o) => o?.label || ''}
            renderInput={(params) => <TextField {...params} label="Assign to" size="small" />}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={bulkBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={runBulkAssign} disabled={bulkBusy}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onClose={() => !bulkBusy && setBulkDeleteOpen(false)}>
        <DialogTitle>Delete selected tasks?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Remove {selectedIds.length} task{selectedIds.length === 1 ? '' : 's'}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkBusy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={runBulkDelete} disabled={bulkBusy}>
            {bulkBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editTask)} onClose={() => setEditTask(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit task</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editDetailLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {editTask?.task_name}
              </Typography>
              <TextField
                label="Due date"
                type="date"
                size="small"
                fullWidth
                value={editDue}
                onChange={(e) => setEditDue(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Priority"
                select
                size="small"
                fullWidth
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </TextField>
              <Autocomplete
                options={staffOptions}
                value={editAssignee}
                onChange={(_, v) => setEditAssignee(v)}
                getOptionLabel={(o) => o?.label || ''}
                renderInput={(params) => <TextField {...params} label="Assigned to" size="small" />}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTask(null)} disabled={editDetailLoading || editSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editDetailLoading || editSaving || !editTask}
          >
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTask)} onClose={() => setDeleteTask(null)}>
        <DialogTitle>Delete task?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {deleteTask?.task_name ? `Remove “${deleteTask.task_name}”? This cannot be undone.` : 'Remove this task?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTask(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default TasksPage;
